import { envFlags } from '@thoughtspot/env-flags';
import { isChartBlurOnResizeEnabled } from '@thoughtspot/session-service';
import { SnapshotManagerV2 } from '@thoughtspot/snapshot';
import { chartProviders, getChartProvider, } from '../../core/chart-type-specification-service';
import { ChartType } from '../../types';
import { createLogger } from '../../utils/logger';
const logger = createLogger('canvas-snapshot');
const SPOTTER_X_FLAG = 'isSpotterXEnabled';
export function isSnapshotResizeEnabled() {
    return (envFlags.getValue(SPOTTER_X_FLAG) === true &&
        isChartBlurOnResizeEnabled());
}
export const SNAPSHOT_STABLE_MS = 100;
const IDLE_PREPARE_TIMEOUT_MS = 1000;
const LEGEND_SCROLLABLE_SELECTORS = ['.os-viewport'];
const HIGHCHART_SCROLLABLE_SELECTORS = [
    ...LEGEND_SCROLLABLE_SELECTORS,
    '.highcharts-scrolling',
];
const PIVOT_SCROLLABLE_SELECTORS = [
    ...LEGEND_SCROLLABLE_SELECTORS,
    '.dx-scrollable-container',
    '.dx-area-row-cell',
    '.dx-area-description-cell',
    '.dx-data-header',
];
const NETWORK_SCROLLABLE_SELECTORS = [
    ...LEGEND_SCROLLABLE_SELECTORS,
    '.bk-network-map-container',
    '.bk-sankey-chart',
];
const SNAPSHOT_GEO_CHART_TYPES = new Set([
    ChartType.GeoArea,
    ChartType.GeoBubble,
    ChartType.GeoHeatmap,
]);
let providerTables;
const getProviderTables = () => {
    if (!providerTables) {
        providerTables = {
            snapshotProviders: new Set([
                chartProviders.HIGHCHART,
                chartProviders.INSIGHT,
                chartProviders.REACT_COMPONENT,
                chartProviders.GEO_MAP,
                chartProviders.NETWORK,
                chartProviders.PIVOT_TABLE,
            ]),
            scrollableSelectors: {
                [chartProviders.HIGHCHART]: HIGHCHART_SCROLLABLE_SELECTORS,
                [chartProviders.INSIGHT]: HIGHCHART_SCROLLABLE_SELECTORS,
                [chartProviders.REACT_COMPONENT]: LEGEND_SCROLLABLE_SELECTORS,
                [chartProviders.PIVOT_TABLE]: PIVOT_SCROLLABLE_SELECTORS,
                [chartProviders.NETWORK]: NETWORK_SCROLLABLE_SELECTORS,
            },
            singletonChartTypes: new Map([
                [chartProviders.REACT_COMPONENT, ChartType.Kpi],
                [chartProviders.NETWORK, ChartType.Sankey],
                [chartProviders.PIVOT_TABLE, ChartType.PivotTable],
            ]),
        };
    }
    return providerTables;
};
const DEFAULT_REPAINT_TIMEOUT_MS = 300;
export function getBitmapCapableChart(chart) {
    const candidate = chart;
    return candidate && typeof candidate.captureBitmap === 'function'
        ? candidate
        : null;
}
export function getRepaintSignalChart(chart) {
    const candidate = chart;
    return candidate && typeof candidate.renderCompleteOnce === 'function'
        ? candidate
        : null;
}
export function waitForChartRepaint(chart) {
    var _a;
    if (!chart) {
        return Promise.resolve();
    }
    const timeoutMs = (_a = chart.repaintTimeoutMs) !== null && _a !== void 0 ? _a : DEFAULT_REPAINT_TIMEOUT_MS;
    return Promise.race([
        chart.renderCompleteOnce().catch(() => undefined),
        new Promise(resolve => {
            window.setTimeout(resolve, timeoutMs);
        }),
    ]);
}
export function createCanvasSnapshot(options) {
    var _a, _b, _c, _d;
    const { isLiveboardContext, isPrintMode, chartType, hasInsightData, mountEl, getRenderedSize, reflowChart, scheduleSettle, afterSettle, captureBitmap, } = options;
    if (!isLiveboardContext || isPrintMode) {
        return null;
    }
    if (!isSnapshotResizeEnabled()) {
        return null;
    }
    const provider = getChartProvider(chartType, hasInsightData);
    const tables = getProviderTables();
    if (!tables.snapshotProviders.has(provider)) {
        return null;
    }
    const singletonType = tables.singletonChartTypes.get(provider);
    if (singletonType !== undefined && chartType !== singletonType) {
        return null;
    }
    const usesBitmapCapture = provider === chartProviders.GEO_MAP;
    if (usesBitmapCapture) {
        if (!SNAPSHOT_GEO_CHART_TYPES.has(chartType) || !captureBitmap) {
            return null;
        }
    }
    const manager = new SnapshotManagerV2({
        getMount: () => mountEl,
        getRoot: () => mountEl.firstElementChild,
        resize: () => {
            reflowChart()
                .catch(err => logger.error('settle reflow failed', err))
                .then(() => manager.notifyRenderComplete())
                .catch(() => undefined);
        },
        getSize: () => getRenderedSize(),
        captureBitmap: usesBitmapCapture ? captureBitmap : undefined,
    }, {
        scaleResize: {
            scrollableSelectors: tables.scrollableSelectors[provider],
        },
    });
    let frozen = false;
    let disposed = false;
    let initialRendered = false;
    let stabilityTimer;
    let prepareIdle;
    const cancelIdle = (_b = (_a = window.cancelIdleCallback) === null || _a === void 0 ? void 0 : _a.bind(window)) !== null && _b !== void 0 ? _b : window.clearTimeout.bind(window);
    const requestIdle = (_d = (_c = window.requestIdleCallback) === null || _c === void 0 ? void 0 : _c.bind(window)) !== null && _d !== void 0 ? _d : (callback => window.setTimeout(callback, 200));
    const armPrepare = () => {
        initialRendered = true;
        if (disposed)
            return;
        if (prepareIdle !== undefined) {
            cancelIdle(prepareIdle);
        }
        prepareIdle = requestIdle(() => {
            if (!frozen && !disposed) {
                manager.prepare(null).catch(err => {
                    logger.error('snapshot prepare failed', err);
                });
            }
        }, { timeout: IDLE_PREPARE_TIMEOUT_MS });
    };
    const settle = () => {
        if (!frozen)
            return;
        frozen = false;
        scheduleSettle(async () => {
            try {
                await manager.unfreeze();
            }
            catch (err) {
                logger.error('snapshot unfreeze failed', err);
            }
            try {
                afterSettle === null || afterSettle === void 0 ? void 0 : afterSettle();
            }
            catch (err) {
                logger.error('canvas-snapshot afterSettle failed', err);
            }
            armPrepare();
        });
    };
    return {
        onResizeTick: (width, height) => {
            if (disposed || !initialRendered)
                return false;
            if (!frozen) {
                frozen = manager.freeze(null);
            }
            if (!frozen) {
                return false;
            }
            manager.resize(width, height);
            window.clearTimeout(stabilityTimer);
            stabilityTimer = window.setTimeout(settle, SNAPSHOT_STABLE_MS);
            return true;
        },
        armPrepare,
        dispose: () => {
            disposed = true;
            frozen = false;
            window.clearTimeout(stabilityTimer);
            if (prepareIdle !== undefined) {
                cancelIdle(prepareIdle);
            }
            manager.dispose();
        },
    };
}
//# sourceMappingURL=canvas-snapshot.js.map